import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { fetchTeamMembers } from './teamMembers.api'
import { mapTeamMemberToRow, type TeamMemberRow } from './teamMembers.mappers'
import type {
  ListTeamMembersParams,
  TeamListRoleFilter,
  TeamListSortBy,
  TeamListSortOrder,
  TeamListStatusFilter,
} from './teamMembers.types'

export interface TeamMembersQueryResult {
  rows: TeamMemberRow[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export type TeamMembersQueryKey = ReturnType<typeof queryKeys.team.members>

export function teamMembersKeyInput(params: ListTeamMembersParams) {
  return {
    page: params.page,
    pageSize: params.pageSize,
    role: params.role ?? '',
    status: params.status ?? '',
    search: params.search ?? '',
    sortBy: params.sortBy ?? 'joinDate',
    sortOrder: params.sortOrder ?? 'desc',
  }
}

/** Maps React Query cache key → API params (single source of truth for fetches). */
export function listParamsFromMembersQueryKey(
  queryKey: TeamMembersQueryKey,
): ListTeamMembersParams {
  const [, , page, pageSize, role, status, search, sortBy, sortOrder] = queryKey
  return {
    page,
    pageSize,
    role: role ? (role as TeamListRoleFilter) : undefined,
    status: status ? (status as TeamListStatusFilter) : undefined,
    search: search || undefined,
    sortBy: (sortBy || 'joinDate') as TeamListSortBy,
    sortOrder: (sortOrder || 'desc') as TeamListSortOrder,
  }
}

export function useTeamMembers(params: ListTeamMembersParams) {
  const keyInput = teamMembersKeyInput(params)

  return useQuery<TeamMembersQueryResult, Error, TeamMembersQueryResult, TeamMembersQueryKey>({
    queryKey: queryKeys.team.members(keyInput),
    queryFn: async ({ queryKey, signal }): Promise<TeamMembersQueryResult> => {
      const fetchParams = listParamsFromMembersQueryKey(queryKey)
      const { items, meta } = await fetchTeamMembers(fetchParams, signal)
      return {
        rows: items.map(mapTeamMemberToRow),
        total: meta?.total ?? 0,
        page: meta?.page ?? fetchParams.page,
        perPage: meta?.per_page ?? fetchParams.pageSize,
        totalPages: meta?.total_pages ?? 1,
      }
    },
    staleTime: 0,
  })
}
